// Funções e constantes compartilhadas pelas telas do Financeiro.
import api from '../../services/api';

export const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
export const MESES_ABREV = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Mesma regra do backend (ReceitaMensal.DIAS_A_RECEBER em projetos/models.py).
// Se mudar lá, mude aqui também.
export const DIAS_A_RECEBER = 7;

// Identidade visual Lean Way.
export const COR = {
  ink: '#1a1a2e',
  navy: '#13375F',
  destaque: '#e94560',
  fundo: '#f8f9fa',
  borda: '#e5e7eb',
  texto2: '#64748b',
};

// Semáforo. `cor` é a cor cheia (barras, bolinhas); `texto` é uma versão mais
// escura para texto pequeno ficar legível; `fundo` é o tom claro de apoio.
export const STATUS_ORDEM = ['em_aberto', 'a_receber', 'a_vencer', 'recebido'];
export const STATUS = {
  em_aberto: {
    label: 'Em aberto', descricao: 'Venceu e ainda não foi recebida',
    cor: '#E24B4A', texto: '#B42B2A', fundo: '#FCEBEB',
  },
  a_receber: {
    label: 'A receber', descricao: `Vence em até ${DIAS_A_RECEBER} dias`,
    cor: '#BA7517', texto: '#8A5410', fundo: '#FAEEDA',
  },
  a_vencer: {
    label: 'A vencer', descricao: `Vence daqui a mais de ${DIAS_A_RECEBER} dias`,
    cor: '#639922', texto: '#4A7516', fundo: '#EAF3DE',
  },
  recebido: {
    label: 'Recebido', descricao: 'Pagamento já recebido',
    cor: '#13375F', texto: '#13375F', fundo: '#E3ECF6',
  },
};

// Projeto cancelado: a empresa não cobra a NF que ainda não foi recebida. Essas notas não entram nos
// totais (faturado, em aberto, vencido, semáforo) e aparecem com esta etiqueta cinza.
export const STATUS_NAO_COBRADA = {
  label: 'Não será cobrada', descricao: 'Projeto cancelado: a nota não será cobrada',
  cor: '#94a3b8', texto: '#64748b', fundo: '#EEF1F5',
};

// Etiqueta ao lado do nome quando o projeto (ou contrato) já terminou.
export const ETIQUETA_ENCERRADO = {
  concluido: { label: 'Concluído', fundo: '#DCFCE7', texto: '#166534' },
  cancelado: { label: 'Cancelado', fundo: '#FEE2E2', texto: '#991B1B' },
};

// ---------------------------------------------------------------- formatação
export const formatarReal = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatarPercentual = (valor) =>
  `${Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%`;

export const formatarData = (iso) => {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

export const rotuloCompetencia = (mes, ano) => `${MESES_ABREV[mes] || mes}/${ano}`;

export const formatarTamanho = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

// ---------------------------------------------------------------------- datas
const doisDigitos = (n) => String(n).padStart(2, '0');
export const paraISO = (data) => `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;
export const hojeISO = () => paraISO(new Date());

// Quantos dias faltam até a data (negativo = já passou). Compara só o dia,
// sem horário, para não errar perto da meia-noite.
export const diasAte = (iso) => {
  if (!iso) return null;
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const agora = new Date();
  const hoje = Date.UTC(agora.getFullYear(), agora.getMonth(), agora.getDate());
  return Math.round((Date.UTC(a, m - 1, d) - hoje) / 86400000);
};

// Mesma lógica do backend (ReceitaMensal.status_automatico).
export const calcularStatusAutomatico = ({ data_vencimento, data_recebimento }) => {
  if (data_recebimento) return 'recebido';
  if (!data_vencimento) return 'a_receber';
  const dias = diasAte(data_vencimento);
  if (dias < 0) return 'em_aberto';
  if (dias <= DIAS_A_RECEBER) return 'a_receber';
  return 'a_vencer';
};

export const textoPrazo = ({ status, dias, data_recebimento }) => {
  if (status === 'recebido') return data_recebimento ? `Recebido em ${formatarData(data_recebimento)}` : 'Recebido';
  if (dias === null || dias === undefined) return 'Sem vencimento informado';
  if (dias < 0) return `Venceu há ${-dias} ${-dias === 1 ? 'dia' : 'dias'}`;
  if (dias === 0) return 'Vence hoje';
  if (dias === 1) return 'Vence amanhã';
  return `Vence em ${dias} dias`;
};

// ------------------------------------------------------------------- totais
// Nota recebida (pela data ou por correção manual) em que ninguém informou o valor líquido.
export const faltaLiquido = (r) => (
  r.status === 'recebido' && (r.valor_liquido_recebido === null || r.valor_liquido_recebido === undefined || r.valor_liquido_recebido === '')
);

// Resume uma lista de NFs (receitas) em números prontos para a tela.
export const resumir = (receitas = []) => {
  const porStatus = {};
  STATUS_ORDEM.forEach((s) => { porStatus[s] = { qtd: 0, valor: 0 }; });
  let faturado = 0;
  let recebido = 0;
  let quitado = 0;
  let proximo = null;
  let maisAntigaVencida = null;
  const semLiquido = { qtd: 0, valor: 0 };

  receitas.forEach((r) => {
    const valor = Number(r.valor || 0);
    faturado += valor;
    if (r.valor_liquido_recebido !== null && r.valor_liquido_recebido !== undefined && (r.data_recebimento || r.status === 'recebido')) {
      recebido += Number(r.valor_liquido_recebido);
    }
    const status = porStatus[r.status] ? r.status : 'a_receber';
    porStatus[status].qtd += 1;
    porStatus[status].valor += valor;
    if (status === 'recebido') quitado += valor;
    if (faltaLiquido(r)) { semLiquido.qtd += 1; semLiquido.valor += valor; }
    if (status !== 'recebido' && r.data_vencimento) {
      if (status === 'em_aberto') {
        if (!maisAntigaVencida || r.data_vencimento < maisAntigaVencida) maisAntigaVencida = r.data_vencimento;
      } else if (!proximo || r.data_vencimento < proximo.data) {
        proximo = { data: r.data_vencimento, valor };
      }
    }
  });

  const aberto = faturado - quitado;
  return {
    qtd: receitas.length,
    faturado,
    recebido,                       // soma do valor líquido recebido
    aberto,                         // NFs ainda não recebidas (valor da NF)
    vencido: porStatus.em_aberto.valor,
    porStatus,
    pctQuitado: faturado ? (quitado / faturado) * 100 : 0,
    proximoVencimento: proximo,
    maisAntigaVencida,
    semLiquido,                     // notas recebidas sem valor líquido informado
  };
};

// ------------------------------------------------------- projeto cancelado
// Contrato novo: cancelado quando todos os projetos foram cancelados; concluído quando todos terminaram
// (concluídos ou cancelados); senão continua em andamento. Contrato sem projeto ainda não terminou.
export const statusDoContratoNovo = (projetos = []) => {
  if (!projetos.length) return 'em_andamento';
  if (projetos.every((p) => p.status === 'cancelado')) return 'cancelado';
  if (projetos.every((p) => p.status === 'concluido' || p.status === 'cancelado')) return 'concluido';
  return 'em_andamento';
};

// Separa as NFs de um item em "valem" e "não serão cobradas". É só uma regra de exibição: nada muda no banco,
// então se o projeto for reativado as notas voltam a valer sozinhas.
//  - itemCancelado: o projeto (ou o contrato inteiro) está cancelado;
//  - projetosCancelados: ids dos projetos cancelados de um contrato novo (a NF do "contrato inteiro" não tem projeto).
export const classificarNotas = (receitas = [], { itemCancelado = false, projetosCancelados = [] } = {}) => {
  const ids = new Set(projetosCancelados);
  const todas = (receitas || []).map((r) => ({
    ...r,
    nao_cobrada: r.status !== 'recebido' && (itemCancelado || (r.projeto != null && ids.has(r.projeto))),
  }));
  return {
    receitas: todas,
    receitasValidas: todas.filter((r) => !r.nao_cobrada),
    naoCobradas: todas.filter((r) => r.nao_cobrada),
  };
};

// ----------------------------------------------------------------- contrato
// O valor do contrato é informado pelo Comercial ao cadastrar a venda.
// Projetos antigos podem ainda não ter valor (null).
export const contratoInformado = (projeto) => (
  projeto?.valor_contrato !== null && projeto?.valor_contrato !== undefined && projeto?.valor_contrato !== ''
);

// Situação do contrato de UM projeto, dado o total já faturado em NFs.
export const situacaoContrato = (projeto, faturado) => {
  if (!contratoInformado(projeto)) return { informado: false };
  const contrato = Number(projeto.valor_contrato);
  return {
    informado: true,
    contrato,
    faturado,
    saldo: contrato - faturado,                                   // negativo = faturou além do contrato
    pctFaturado: contrato > 0 ? (faturado / contrato) * 100 : 0,
    estourou: faturado > contrato + 0.005,
  };
};

// Resume os contratos de vários projetos (projetos cancelados não entram).
export const resumirContratos = (projetos = []) => {
  let total = 0;
  let faturado = 0;
  let saldo = 0;
  let comValor = 0;
  let semValor = 0;
  const estouros = [];

  projetos.filter((p) => p.status !== 'cancelado').forEach((p) => {
    if (!contratoInformado(p)) { semValor += 1; return; }
    const fat = (p.receitasValidas || p.receitas || []).reduce((soma, r) => soma + Number(r.valor || 0), 0);
    const contrato = Number(p.valor_contrato);
    comValor += 1;
    total += contrato;
    faturado += fat;
    saldo += Math.max(contrato - fat, 0);
    if (fat > contrato + 0.005) estouros.push({ projeto: p, excesso: fat - contrato });
  });

  return {
    comValor, semValor, total, faturado, saldo, estouros,
    pctFaturado: total > 0 ? (faturado / total) * 100 : 0,
  };
};

// ------------------------------------------------------------- erros da API
// Transforma a resposta de erro do Django em { campos: {campo: texto}, mensagem }.
export const extrairErros = (err, padrao = 'Não foi possível concluir a operação.') => {
  const dados = err?.response?.data;
  if (!dados || typeof dados !== 'object') return { campos: {}, mensagem: padrao };
  const campos = {};
  let geral = typeof dados.detail === 'string' ? dados.detail : '';
  Object.entries(dados).forEach(([chave, valor]) => {
    if (chave === 'detail') return;
    const texto = Array.isArray(valor) ? String(valor[0]) : (typeof valor === 'string' ? valor : '');
    if (chave === 'non_field_errors') geral = geral || texto;
    else campos[chave] = texto;
  });
  return {
    campos,
    mensagem: geral || (Object.keys(campos).length ? 'Corrija os campos destacados.' : padrao),
  };
};

// ---------------------------------------------------------------- downloads
export const salvarBlob = (blob, nome) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

// Quando pedimos um arquivo (blob) e o servidor responde erro, a mensagem
// também vem como blob. Isto lê o texto de dentro.
const mensagemDeBlob = async (err, padrao) => {
  try {
    const dados = err?.response?.data;
    if (dados instanceof Blob) {
      const json = JSON.parse(await dados.text());
      return json.detail || padrao;
    }
  } catch (e) { /* usa a mensagem padrão */ }
  return err?.response?.data?.detail || padrao;
};

// Abre a NF anexada. PDF e imagens abrem numa aba nova; XML é baixado.
// O arquivo passa pelo servidor com o login do usuário (não existe link público).
export const abrirNotaFiscal = async (receita) => {
  const nome = receita.arquivo_nf_nome || `nf-${receita.id}`;
  const previa = /\.(pdf|png|jpe?g)$/i.test(nome);
  // A aba precisa ser aberta já no clique, antes da espera, senão o navegador bloqueia.
  const janela = previa ? window.open('', '_blank') : null;
  try {
    const res = await api.get(`/receitas/${receita.id}/arquivo-nf/`, { responseType: 'blob' });
    if (janela) {
      const url = URL.createObjectURL(res.data);
      janela.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } else {
      salvarBlob(res.data, nome);
    }
  } catch (err) {
    if (janela) janela.close();
    throw new Error(await mensagemDeBlob(err, 'Não foi possível abrir a NF.'));
  }
};

export const montarParametrosRelatorio = (filtros) => {
  const params = { campo_data: filtros.campo_data || 'competencia' };
  if (filtros.data_de) params.data_de = filtros.data_de;
  if (filtros.data_ate) params.data_ate = filtros.data_ate;
  if (filtros.projeto) {
    // Na tela, projeto antigo = "p5" e contrato novo = "c3". A API recebe cada um num parâmetro.
    const chave = String(filtros.projeto);
    if (chave.startsWith('c')) params.contrato = chave.slice(1);
    else params.projeto = chave.replace(/^p/, '');
  }
  if (filtros.cliente) params.cliente = filtros.cliente;
  if (filtros.status?.length) params.status = filtros.status.join(',');
  return params;
};

export const buscarRelatorio = async (filtros) => {
  const res = await api.get('/financeiro/relatorio/', { params: montarParametrosRelatorio(filtros) });
  return res.data;
};

export const baixarRelatorio = async (formato, filtros) => {
  try {
    const res = await api.get('/financeiro/relatorio/', {
      params: { ...montarParametrosRelatorio(filtros), formato },
      responseType: 'blob',
    });
    salvarBlob(res.data, `relatorio-financeiro_${hojeISO()}.${formato}`);
  } catch (err) {
    throw new Error(await mensagemDeBlob(err, 'Não foi possível gerar o arquivo.'));
  }
};
